import { Button } from '@mui/material';
import { isEmpty, Modal } from '@vxui/components';
import { FormikErrors, FormikProvider, useFormik } from 'formik';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NewUser, UserRole } from '../../models';
import { useCreateUserMutation } from '../../services';
import { maxPasswordLength, minPasswordLength } from './PasswordForm.helpers';
import { UserForm } from './UserForm';

type CreateUserModalProps = {
  onClose: () => void;
};

export const createUserModalId = 'createUserModal';

export const CreateUserModal: FC<CreateUserModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [createUser] = useCreateUserMutation();

  const formik = useFormik<NewUser>({
    initialValues: {
      login: '',
      username: '',
      role: UserRole.User,
      password: '',
      confirmedPassword: '',
    },
    onSubmit: async values => {
      const { confirmedPassword: _, ...user } = values;
      try {
        await createUser({ ...user, username: user.login }).unwrap();
        onClose();
      } catch (e) {
        console.error('Unable to create user', e);
      }
    },
    validate: values => {
      const error = (fieldName: string, errorType: string, values = {}) =>
        t(`auth.forms.user.fields.${fieldName}.errors.${errorType}`, values);
      const errors: FormikErrors<NewUser> = {};

      // if (isEmpty(values.username)) errors.username = error('username', 'empty');

      if (isEmpty(values.login)) errors.login = error('login', 'empty');
      else if (values.login.trim().length < 3) errors.login = error('login', 'tooShort');
      else if (values.login.trim().length > 18) errors.login = error('login', 'tooLong');

      if (isEmpty(values.password)) errors.password = error('password', 'empty');
      else if (values.password.trim().length < minPasswordLength)
        errors.password = error('password', 'tooShort', { min: minPasswordLength });
      else if (values.password.trim().length > maxPasswordLength)
        errors.password = error('password', 'tooLong', { max: maxPasswordLength });

      if (values.password !== values.confirmedPassword)
        errors.confirmedPassword = error('confirmedPassword', 'dontMatch');

      // if (isEmpty(values.role)) errors.role = error('role', 'empty');

      return errors;
    },
  });

  const submit = useCallback(() => formik.submitForm(), [formik]);

  return (
    <Modal
      open
      title={t('auth.modal.createUser.title')}
      actions={
        <>
          <Button onClick={onClose} color="secondary" variant="outlined">
            {t('button.close')}
          </Button>
          <Button onClick={submit} color="secondary" variant="contained">
            {t('button.save')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <UserForm />
      </FormikProvider>
    </Modal>
  );
};
